import React, { useState } from "react";
import { PencilIcon } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface RenameChatDialogProps {
  conversationId: string;
  currentTitle: string;
  open: boolean;
  onRename: (newTitle: string) => void;
  setOpen: (isOpen: boolean) => void;
  renameConversation: (conversationId: string, data: any) => void;
}

const RenameChatDialog: React.FC<RenameChatDialogProps> = ({
  conversationId,
  currentTitle,
  onRename,
  open,
  setOpen,
  renameConversation,
}) => {
  const [title, setTitle] = useState(currentTitle);
  const [error, setError] = useState<string | null>(null);
  const [originalTitle, setOriginalTitle] = useState(title);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setError("Title cannot be empty");
      return;
    }

    onRename(title.trim());
    setOpen(false);
    setError(null);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        setTitle(originalTitle);
      }
      setOpen(isOpen);
    }}>
      <DialogTrigger asChild>
        <Button variant="ghost" className="flex-row justify-between min-w-9">
          <PencilIcon className="h-4 w-4" />
          <span>Rename</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename Chat</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="title">Chat Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="Enter chat title"
                autoFocus
              />
              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setTitle(originalTitle);
                setOpen(false);
              }}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-brand-secondary hover:bg-brand-tertiary rounded-full"
              onClick={() =>
                renameConversation(conversationId, {
                  conversation_id: conversationId,
                  title: title,
                })
              }
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RenameChatDialog;
